import { Controller } from "@hotwired/stimulus"

const STATE_TEXT_DATA_ATTRS = {
  pending:           "pendingText",
  saving:            "savingText",
  saved:             "savedText",
  errorValidation:   "errorValidationText",
  errorNetwork:      "errorNetworkText"
}

export default class extends Controller {
  static targets = ["status"]
  static values  = {
    delay:        { type: Number, default: 1500 },
    maxBackoff:   { type: Number, default: 30000 }
  }

  connect() {
    if (!this._isAutosavable()) return

    this.timeout    = null
    this.inFlight   = null
    this.pendingDirty = false
    this.backoff    = 1000

    this.onInput = this.onInput.bind(this)
    this.element.addEventListener("input",         this.onInput)
    this.element.addEventListener("trix-change",   this.onInput)
  }

  disconnect() {
    clearTimeout(this.timeout)
    if (this.element && this.onInput) {
      this.element.removeEventListener("input",       this.onInput)
      this.element.removeEventListener("trix-change", this.onInput)
    }
  }

  onInput() {
    this.pendingDirty = true
    this._setStatus("pending", "Niezapisane zmiany…")
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this._save(), this.delayValue)
  }

  async _save() {
    if (!this.pendingDirty) return
    if (this.inFlight) {
      this.timeout = setTimeout(() => this._save(), 200)
      return
    }

    this.pendingDirty = false
    this._setStatus("saving", "Zapisywanie…")

    const form = this.element
    const formData = new FormData(form)
    const csrfMeta = document.querySelector("meta[name='csrf-token']")
    const csrf = csrfMeta ? csrfMeta.content : ""

    const method = (formData.get("_method") || form.method || "patch").toString().toUpperCase()
    const url    = form.action

    this.inFlight = fetch(url, {
      method,
      body: formData,
      headers: {
        "X-Autosave":   "1",
        "Accept":       "application/json",
        "X-CSRF-Token": csrf
      },
      credentials: "same-origin"
    })

    try {
      const response = await this.inFlight
      this.inFlight = null

      if (response.status === 204) {
        this.backoff = 1000
        this._setStatus("saved", this._formatSavedAt(new Date()))
      } else if (response.status === 422) {
        this._setStatus("errorValidation", "Nie zapisano — popraw błędy walidacji")
      } else {
        this._scheduleRetry()
      }
    } catch (_err) {
      this.inFlight = null
      this._scheduleRetry()
    }
  }

  _scheduleRetry() {
    this._setStatus("errorNetwork", "Brak połączenia, ponawiam…")
    this.pendingDirty = true
    const delay = Math.min(this.backoff, this.maxBackoffValue)
    this.backoff = Math.min(this.backoff * 2, this.maxBackoffValue)
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this._save(), delay)
  }

  _setStatus(state, fallback) {
    if (!this.hasStatusTarget) return
    const el = this.statusTarget
    const dataAttr = STATE_TEXT_DATA_ATTRS[state]
    const text = (dataAttr && el.dataset[dataAttr]) ? el.dataset[dataAttr] : fallback
    el.textContent = text
    el.dataset.autosaveState = state
  }

  _formatSavedAt(date) {
    const hh = String(date.getHours()).padStart(2, "0")
    const mm = String(date.getMinutes()).padStart(2, "0")
    const template = this.hasStatusTarget && this.statusTarget.dataset.savedTemplate
    if (template) return template.replace("%{time}", `${hh}:${mm}`)
    return `Zapisano ${hh}:${mm}`
  }

  _isAutosavable() {
    if (!(this.element instanceof HTMLFormElement)) return false
    const explicitMethod = this.element.querySelector("input[name='_method']")
    const method = (explicitMethod ? explicitMethod.value : this.element.method).toLowerCase()
    return method === "patch" || method === "put"
  }
}
